import { Auth } from '@grammarly/identity-ui'

import { gnarSpec } from '../../lib/tracking/gnar'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { assertNeverThrow } from '../../lib/utils/assert-never'

export class UnifiedAuthTracker implements Auth.Tracking.Auth {
  constructor(
    private pageId: string,
    private pageSlug: string,
    private isGdprExperiment: boolean
  ) {}

  trackFormShow = (formKey: Auth.Tracking.FormType) => {
    void gnarSpec.unifiedAuthFormShow(formKey, this.isGdprExperiment, this.pageId, this.pageSlug)
  }

  trackAttempt = (
    authContext: Auth.Context,
    formKey: Auth.Tracking.FormType,
    userChoices?: Auth.Tracking.UserChoices,
    initFlowFormKey?: Auth.Tracking.FormType
  ) => {
    this.trackAuthMetric(authContext, formKey, CmsMetrics.ClientMetricsActions.Attempt)
    void gnarSpec.unifiedAuthRequestAttempt(
      authContext.action,
      this.getEmailOptOut(userChoices),
      this.getFlowType(authContext),
      formKey,
      this.isGdprExperiment,
      this.pageId,
      this.pageSlug,
      this.getHasSignUpEmailVerificationCode(authContext),
      initFlowFormKey,
      undefined,
      authContext.isSecureSignIn,
      this.getIsSignUpEmailVerificationResendCodeRequest(authContext),
      this.getMfaMode(authContext),
      undefined,
      this.getSocialFlowType(authContext),
      this.getTermsOfServiceAccepted(formKey, userChoices),
      this.getUseSignUpEmailVerification(authContext)
    )
  }

  trackSuccess = (
    authContext: Auth.Context,
    formKey: Auth.Tracking.FormType,
    userChoices?: Auth.Tracking.UserChoices,
    initFlowFormKey?: Auth.Tracking.FormType
  ) => {
    this.trackAuthMetric(authContext, formKey, CmsMetrics.ClientMetricsActions.Success)
    void gnarSpec.unifiedAuthRequestSuccess(
      authContext.action,
      this.getEmailOptOut(userChoices),
      this.getFlowType(authContext),
      formKey,
      this.isGdprExperiment,
      this.pageId,
      this.pageSlug,
      this.getHasSignUpEmailVerificationCode(authContext),
      initFlowFormKey,
      undefined,
      authContext.isSecureSignIn,
      this.getIsSignUpEmailVerificationResendCodeRequest(authContext),
      this.getMfaMode(authContext),
      undefined,
      this.getSocialFlowType(authContext),
      this.getTermsOfServiceAccepted(formKey, userChoices),
      this.getUseSignUpEmailVerification(authContext)
    )
  }

  trackFail = (
    err: Auth.Error.AuthCommonError,
    authContext: Auth.Context,
    formKey: Auth.Tracking.FormType,
    userChoices?: Auth.Tracking.UserChoices,
    initFlowFormKey?: Auth.Tracking.FormType
  ) => {
    this.trackAuthMetric(authContext, formKey, CmsMetrics.ClientMetricsActions.Failure)
    void gnarSpec.unifiedAuthRequestFailure(
      authContext.action,
      this.getEmailOptOut(userChoices),
      err.errCode,
      this.getFlowType(authContext),
      formKey,
      this.isGdprExperiment,
      this.pageId,
      this.pageSlug,
      this.getHasSignUpEmailVerificationCode(authContext),
      initFlowFormKey,
      undefined,
      err.isRecoverable,
      authContext.isSecureSignIn,
      this.getIsSignUpEmailVerificationResendCodeRequest(authContext),
      this.getMfaMode(authContext),
      undefined,
      this.getSocialFlowType(authContext),
      this.getTermsOfServiceAccepted(formKey, userChoices),
      this.getUseSignUpEmailVerification(authContext)
    )
  }

  trackSubmitButtonClick = (
    authContext: Auth.Context,
    formKey: Auth.Tracking.FormType,
    fieldErrors?: Auth.Tracking.FieldError[],
    userChoices?: Auth.Tracking.UserChoices,
    countSubmits?: number
  ): Promise<void> => {
    return gnarSpec.unifiedAuthFormSubmitButtonClick(
      authContext.action,
      this.getEmailOptOut(userChoices),
      this.getFlowType(authContext),
      formKey,
      this.isGdprExperiment,
      this.pageId,
      this.pageSlug,
      countSubmits,
      fieldErrors,
      this.getHasSignUpEmailVerificationCode(authContext),
      undefined,
      authContext.isSecureSignIn,
      this.getIsSignUpEmailVerificationResendCodeRequest(authContext),
      this.getMfaMode(authContext),
      undefined,
      this.getSocialFlowType(authContext),
      this.getTermsOfServiceAccepted(formKey, userChoices),
      this.getUseSignUpEmailVerification(authContext)
    )
  }

  trackToSLinkFollow = (linkType: Auth.Tracking.ToSLinkType) => {
    void gnarSpec.unifiedAuthTosLinkClick(linkType, this.pageId, this.pageSlug, this.isGdprExperiment)
  }

  private trackAuthMetric(
    authContext: Auth.Context,
    formKey: Auth.Tracking.FormType,
    action:
      | CmsMetrics.ClientMetricsActions.Attempt
      | CmsMetrics.ClientMetricsActions.Success
      | CmsMetrics.ClientMetricsActions.Failure
  ) {
    const authMetric = this.getAuthMetric(authContext)
    const socialFlowType = this.getSocialFlowType(authContext)
    feMetrics
      .getMetric(authMetric)
      .getCounter(action)
      .setContext({
        formKey,
        pageId: this.pageId,
        flowType: this.getFlowType(authContext),
        ...(socialFlowType && { socialFlowType }),
      })
      .increment()
  }

  private getAuthMetric(authContext: Auth.Context) {
    switch (authContext.action) {
      case 'SIGNIN':
        return CmsMetrics.GeneralClientMetrics.Signin
      case 'SIGNUP':
        return CmsMetrics.GeneralClientMetrics.Signup
      default:
        assertNeverThrow(authContext.action)
    }
  }

  private getEmailOptOut(userChoices?: Auth.Tracking.UserChoices): boolean {
    return this.isGdprExperiment ? !userChoices?.isMarketingEmailsAllowed : false
  }

  private getFlowType(context: Auth.Context): 'email' | 'social' | 'sso' {
    if (Auth.isAuthSocialContext(context)) {
      return 'social'
    }
    return context.isSSO ? 'sso' : 'email'
  }

  private getUseSignUpEmailVerification(context: Auth.Context): boolean {
    return Auth.isAuthEmailContext(context) && !!context.useSignUpEmailVerification
  }

  private getHasSignUpEmailVerificationCode(context: Auth.Context): boolean {
    return Auth.isAuthEmailContext(context) && !!context.hasSignUpEmailVerificationCode
  }

  private getIsSignUpEmailVerificationResendCodeRequest(context: Auth.Context): boolean {
    return Auth.isAuthEmailContext(context) && !!context.isSignUpEmailVerificationResendCodeRequest
  }

  private getSocialFlowType(context: Auth.Context): Auth.Social.Type | undefined {
    if (Auth.isAuthSocialContext(context)) {
      return context.socialType
    }
    return undefined
  }

  private getMfaMode(context: Auth.Context): Auth.MfaSettings.Mode | undefined {
    return Auth.isAuthEmailContext(context) ? context.mfaMode : undefined
  }

  private getTermsOfServiceAccepted(
    formKey: Auth.Tracking.FormType,
    userChoices?: Auth.Tracking.UserChoices
  ): boolean | undefined {
    return formKey === Auth.Tracking.FormType.NewAccount
      ? userChoices?.isTermsOfServiceAccepted === null
        ? true
        : userChoices?.isTermsOfServiceAccepted
      : undefined
  }
}
